import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '../utils/cn';
import { matchesSearch } from '../utils/searchTokens';
import './SelectWithSearch.css';

/**
 * Searchable single/multi select.
 *
 * Ported from the reporting UI; the Tailwind + class-variance-authority styling
 * became plain CSS on tokens.css (see SelectWithSearch.css), and `variant`/`size`/
 * `optionSize` now pick modifier classes instead of generated utility strings.
 *
 * Props
 *   options                   — [{ value, label }]
 *   selectedValues            — array of selected values (one entry for 'single')
 *   onSelectionChange(values) — always called with an array
 *   selectType                — 'single' (default) | 'multiple'
 *   numberOptionsToShowSearch — show the search box once there are more options than this
 *   numberColumnsToDisplay    — 1 (default) | 2 | 3
 *   renderOption(option, isSelected) — custom option body
 */
export default function SelectWithSearch({
  options = [],
  selectedValues = [],
  onSelectionChange,
  placeholder = 'Select option...',
  selectType = 'single',
  numberOptionsToShowSearch = 9,
  numberColumnsToDisplay = 1,
  optionSize = 'md',
  variant = 'primary',
  size = 'md',
  className,
  invalid = false,
  disabled = false,
  renderOption,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [align, setAlign] = useState('left');
  const [panelStyle, setPanelStyle] = useState(null);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option => matchesSearch(option.label, searchTerm));
  const showSearch = options.length > numberOptionsToShowSearch;
  const isSelected = (value) => selectedValues.some(sv => String(sv) === String(value));

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectType === 'single') {
      return options.find(opt => String(opt.value) === String(selectedValues[0]))?.label || placeholder;
    }
    return `${selectedValues.length} selected`;
  };

  const handleOptionClick = (option) => {
    if (selectType === 'single') {
      onSelectionChange?.([option.value]);
      setIsOpen(false);
    } else {
      onSelectionChange?.(
        isSelected(option.value)
          ? selectedValues.filter(v => String(v) !== String(option.value))
          : [...selectedValues, option.value]
      );
    }
    setSearchTerm('');
  };

  // Auto-scroll to the selected option when the dropdown opens or the options load.
  useEffect(() => {
    if (isOpen && dropdownRef.current && selectedValues.length > 0 && filteredOptions.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (!dropdownRef.current) return;
        const selected = dropdownRef.current.querySelector('[data-selected="true"]');
        selected?.scrollIntoView(
          numberColumnsToDisplay > 1
            ? { behavior: 'smooth', block: 'nearest', inline: 'nearest' }
            : { behavior: 'smooth', block: 'center' }
        );
      }, 100);
    }
  }, [isOpen, selectedValues, filteredOptions, options, numberColumnsToDisplay]);

  // Close when clicking outside either the trigger or the portaled panel.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position the portaled panel against the trigger's viewport rect. Rendering into
  // document.body escapes any ancestor stacking context — e.g. SectionsCard's animated
  // columns apply `filter`, which traps a locally-positioned absolute dropdown behind
  // other page content even when it isn't actively animating.
  useLayoutEffect(() => {
    if (!isOpen || !selectRef.current) {
      setPanelStyle(null);
      setAlign('left');
      return undefined;
    }

    function updatePosition() {
      if (!selectRef.current) return;
      const rect = selectRef.current.getBoundingClientRect();
      const next = { position: 'fixed', top: rect.bottom + 4, minWidth: rect.width };
      if (align === 'right') {
        next.right = window.innerWidth - rect.right;
      } else {
        next.left = rect.left;
      }
      setPanelStyle(next);
    }

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, align]);

  // Flip to right-aligned once the panel's real (content-driven) width is known and would
  // otherwise overflow the viewport.
  useLayoutEffect(() => {
    if (!isOpen || !selectRef.current || !dropdownRef.current) return;

    const selectRect = selectRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const nextAlign = selectRect.left + dropdownRect.width > window.innerWidth ? 'right' : 'left';

    if (nextAlign !== align) setAlign(nextAlign);
  }, [isOpen, panelStyle, align]);

  const renderOptionBody = (option) => (
    renderOption ? renderOption(option, isSelected(option.value)) : option.label
  );

  const optionProps = (option) => ({
    className: cn('sws-option', `sws-option--${optionSize}`, isSelected(option.value) && 'sws-option--selected'),
    'data-selected': isSelected(option.value) ? 'true' : 'false',
    'data-value': option.value,
    onClick: () => handleOptionClick(option),
  });

  return (
    <div className={cn('sws', className)} {...props}>
      <button
        type="button"
        ref={selectRef}
        disabled={disabled}
        aria-expanded={isOpen}
        className={cn(
          'sws-trigger',
          `sws-trigger--${variant}`,
          `sws-trigger--${size}`,
          invalid && 'sws-trigger--error'
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={cn('sws-trigger__text', selectedValues.length === 0 && 'sws-trigger__text--placeholder')}>
          {getDisplayText()}
        </span>
        <ChevronDown className={cn('sws-trigger__chevron', isOpen && 'sws-trigger__chevron--open')} />
      </button>

      {isOpen && panelStyle && createPortal(
        <div ref={dropdownRef} style={panelStyle} className="sws-panel">
          {showSearch && (
            <div className="sws-search">
              <Search className="sws-search__icon" />
              <input
                type="text"
                autoFocus
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="sws-search__input"
              />
            </div>
          )}

          <div className={cn('sws-list', numberColumnsToDisplay > 1 && 'sws-list--padded')}>
            {filteredOptions.length === 0 ? (
              <div className="sws-empty">
                Nothing matches “{searchTerm}” — try fewer letters, or clear the search.
              </div>
            ) : numberColumnsToDisplay === 1 ? (
              filteredOptions.map((option, index) => (
                <React.Fragment key={`${option.value}-${index}`}>
                  <div {...optionProps(option)}>{renderOptionBody(option)}</div>
                  {index < filteredOptions.length - 1 && <div className="sws-divider" />}
                </React.Fragment>
              ))
            ) : (
              <div className={cn('sws-grid', `sws-grid--${numberColumnsToDisplay}`)}>
                {filteredOptions.map((option, index) => (
                  <div key={`${option.value}-${index}`} {...optionProps(option)}>
                    {renderOptionBody(option)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
